// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./help-box.component.scss";
import {
  Component,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
} from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";

import { AppStateStoreService } from "../../services/store/app-state/app-state.store.service";
import { TokenService } from "@vault/common/abstractions/token.service";
import { SupportConfigService } from "../../services/api/support-config.service";

const MAX_MSG_LENGTH = 150;
const CHARACTER_COUNT_PER_ROW = 45;
const MAX_ROW_NUM = 8;

@Component({
  selector: "app-help-box",
  templateUrl: "./help-box.component.html",
})
export class HelpBoxComponent implements OnInit {
  protected appState: AppStateStoreService = inject(AppStateStoreService);
  webError: string | null = null;
  firstName: string;
  email: string;
  userId: string;
  message: string;
  submitClicked = false;
  requestProcessing = false;
  supportBoxHeader = "Help & Support";
  rows = 4;
  @Input() prefilledMessage = "";
  @Input() placeholderText: string;
  @Output() changeHelpOpen = new EventEmitter<boolean>();

  constructor(
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public data: {
      supportBoxHeader: string;
      textFieldMessage: string;
      dialogOpen: true;
      closeDialog: CallableFunction;
    },
    private formBuilder: FormBuilder,
    private tokenService: TokenService,
    private supportConfigService: SupportConfigService,
  ) {}

  supportEnquiry = this.formBuilder.group({
    firstName: ["", Validators.required],
    lastName: [""],
    email: ["", [Validators.required, Validators.email]],
    message: ["", Validators.required],
  });

  async ngOnInit(): Promise<void> {
    this.supportBoxHeader = this.data?.supportBoxHeader || "Help & Support";
    this.message = this.data?.textFieldMessage;
    this.firstName = await this.tokenService.getName();
    this.email = await this.tokenService.getEmail();
    this.userId = await this.tokenService.getUserId();
    if (this.prefilledMessage && this.prefilledMessage.length > MAX_MSG_LENGTH) {
      const rowCount = Math.ceil(this.prefilledMessage.length / CHARACTER_COUNT_PER_ROW);
      this.rows = rowCount < MAX_ROW_NUM ? rowCount + 1 : MAX_ROW_NUM;
    }
    this.autoFillUserDetail();
  }

  autoFillUserDetail() {
    this.supportEnquiry.patchValue({
      firstName: this.firstName,
      email: this.email,
      message: this.message,
    });
  }

  closeHelpBox() {
    this.changeHelpOpen.emit(false);
    this.appState.helpBox.set({ isVisible: false });
    if (this.data?.closeDialog) {
      this.data.closeDialog();
    }
  }

  async sendMessage() {
    this.submitClicked = true;
    this.supportEnquiry.markAllAsTouched();

    if (this.supportEnquiry.valid) {
      this.requestProcessing = true;
      this.webError = null;
      try {
        const result = this.supportEnquiry.value;
        const payload = this.supportConfigService.setPayload(result);
        await this.supportConfigService.sendSupport(payload);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        this.webError = "Failed to send your message. Please try again.";
        return;
      } finally {
        this.requestProcessing = false;
      }
      this.closeHelpBox();
    }
  }
}
